/** What a turn could not read, grouped by what it means for the answer. */

export interface ReadFailure {
  tool: string;
  upload_id: string | null;
  reason: string;
  detail: string;
}

export interface ReadFailureGroup {
  label: string;
  /** One per document and reason: which document, and what the platform said. */
  details: { uploadId: string | null; detail: string }[];
}

// Must match the reasons ``agents.tools.failures`` records.
const GROUPS: { reasons: string[]; label: (n: number) => string }[] = [
  {
    reasons: ["error", "empty", "garbled", "context_full"],
    label: (n) => `Could not read ${n} document${n === 1 ? "" : "s"}`,
  },
  {
    reasons: ["truncated"],
    label: (n) =>
      `Read ${n} document${n === 1 ? "" : "s"} that the platform could only partially extract`,
  },
  {
    reasons: ["degraded"],
    label: (n) => `Read ${n} drawing${n === 1 ? "" : "s"} at reduced resolution`,
  },
];

const KNOWN = new Set(GROUPS.flatMap((g) => g.reasons));

// Any other reason still counts: a failure the page does not know how to name must
// not vanish from the warning.
const OTHER = {
  label: (n: number) => `Had trouble reading ${n} document${n === 1 ? "" : "s"}`,
};

const group = (hits: ReadFailure[], label: (n: number) => string): ReadFailureGroup | null => {
  const ids = new Set(hits.map((f) => f.upload_id ?? `${f.tool}:${f.detail}`));
  if (ids.size === 0) return null;
  const seen = new Set<string>();
  const details = hits
    .filter((f) => {
      const key = `${f.upload_id}|${f.detail}`;
      if (seen.has(key)) return false;
      seen.add(key);
      return true;
    })
    .map((f) => ({ uploadId: f.upload_id, detail: f.detail }));
  return { label: label(ids.size), details };
};

/** One line per kind of shortfall, counting documents rather than failed calls, with
 * the platform's reason for each. A web search is not a document, so it is its own. */
export const readFailureGroups = (failures: ReadFailure[]): ReadFailureGroup[] => {
  const documents = failures.filter((f) => f.tool !== "web_search");
  const groups = [
    ...GROUPS.map((g) =>
      group(
        documents.filter((f) => g.reasons.includes(f.reason)),
        g.label,
      ),
    ),
    group(
      documents.filter((f) => !KNOWN.has(f.reason)),
      OTHER.label,
    ),
  ].filter((g): g is ReadFailureGroup => g !== null);
  const searches = failures.filter((f) => f.tool === "web_search").length;
  if (searches > 0) {
    groups.push({
      label: `${searches} web search${searches === 1 ? "" : "es"} failed`,
      details: [],
    });
  }
  return groups;
};
